/**
 * Mixin for synchronizing scenario state between windows
 * This mixin handles the synchronization of PGN data and current move
 * between multiple windows using Vuex and localStorage
 */

import { mapGetters } from "vuex";
import { PgnDataBuilder } from "vue-chess-shared/pgn/PgnDataBuilder";
import Chess from "chess.js";
import { PgnData } from "vue-chess-shared/pgn/PgnData";

export default {
  data() {
    return {
      pgnData: PgnData.createEmpty(new Chess()),
      currentMove: {},
    };
  },

  computed: {
    ...mapGetters([
      "getScenarioState",
      "getCoachingOrientation",
      "getShowMoveTable",
    ]),

    /**
     * Computed property to get the PGN text from the PGN data
     */
    pgnText() {
      return this.pgnData ? this.pgnData.toString() : "";
    },
  },

  watch: {
    getCoachingOrientation: {
      handler(newOrientation) {
        this.$store.commit("setScenarioState", {
          orientation: newOrientation,
        });
      },
    },

    getShowMoveTable: {
      handler(newShowMoveTable) {
        this.$store.commit("setScenarioState", {
          showMoveTable: newShowMoveTable,
        });
      },
    },

    /**
     * Watch for changes in the scenario state from other windows
     */
    getScenarioState: {
      handler(newState) {
        if (!newState) return;

        const { pgn: newPgn, moveId: newMoveId } = newState;

        // Skip PGN and move updates if nothing has changed
        if (newPgn === this.pgnText && newMoveId === this.currentMove?.id) {
          return;
        }

        try {
          // Always reconstruct PGN data when scenario state changes
          if (newPgn && newPgn !== this.pgnText) {
            const pgnDataBuilder = new PgnDataBuilder(newPgn, new Chess());
            this.pgnData = pgnDataBuilder.pgnData();
          }

          // Find the move by ID in the reconstructed PGN data
          const move = newMoveId
            ? this.pgnData.rootMove?.findMoveById(newMoveId)
            : null;

          this.currentMove = move || {};
        } catch (error) {
          console.error("Error processing scenario state:", error);
        }
      },
    },

    /**
     * Watch for local changes in the PGN data and sync to Vuex
     */
    pgnText: {
      handler(newPgnText) {
        if (newPgnText && newPgnText !== this.getScenarioState?.pgn) {
          this.syncState();
        }
      },
    },

    /**
     * Watch for local changes in the current move and sync to Vuex
     */
    currentMove: {
      handler(newMove) {
        if (newMove.id !== this.getScenarioState?.moveId) {
          this.syncState();
        }
      },
    },
  },

  methods: {
    /**
     * Sync both PGN data and current move to Vuex at once
     */
    syncState() {
      // Only sync if we have valid data
      if (!this.pgnData) return;

      const pgn = this.pgnData.toString();
      const moveId =
        this.currentMove && this.currentMove.id ? this.currentMove.id : null;
      const orientation = this.getCoachingOrientation;
      const showMoveTable = this.getShowMoveTable;

      // Skip if nothing has changed
      const currentState = this.getScenarioState;
      if (
        pgn === currentState?.pgn &&
        moveId === currentState?.moveId &&
        orientation === currentState?.orientation &&
        showMoveTable === currentState?.showMoveTable
      )
        return;

      // Update all values in a single batch to ensure consistency
      this.$store.commit("setScenarioState", {
        pgn,
        moveId,
        orientation,
        showMoveTable,
      });
    },
  },
};
